import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { 
  GraduationCap, 
  Clock, 
  Users, 
  BookOpen, 
  PlayCircle, 
  FileText, 
  ArrowLeft,
  CheckCircle,
  Star,
  Award,
  ChevronRight
} from "lucide-react";

const CourseInfo = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCurriculum, setShowCurriculum] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetchCourse();
    fetchSettings();
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}`);
      setCourse(response.data);
    } catch (error) {
      console.error("Failed to load course:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Failed to load settings");
    }
  };

  const primaryColor = settings?.primary_color || "#6d28d9";

  // Default curriculum if none exists
  const defaultCurriculum = [
    {
      module_title: "Getting Started",
      module_description: "Introduction to the course",
      lessons: [
        { title: "Welcome & Course Overview", type: "video", duration: "5 min" },
        { title: "Setting Up Your Environment", type: "video", duration: "10 min" },
        { title: "Course Materials", type: "document", duration: "5 min" }
      ]
    },
    {
      module_title: "Core Concepts",
      module_description: "Fundamental principles and techniques",
      lessons: [
        { title: "Understanding the Basics", type: "video", duration: "15 min" },
        { title: "Practical Examples", type: "video", duration: "20 min" },
        { title: "Hands-on Exercise", type: "assignment", duration: "30 min" }
      ]
    },
    {
      module_title: "Advanced Topics",
      module_description: "Deep dive into advanced concepts",
      lessons: [
        { title: "Advanced Techniques", type: "video", duration: "25 min" },
        { title: "Best Practices", type: "video", duration: "15 min" },
        { title: "Final Project", type: "assignment", duration: "60 min" }
      ]
    }
  ];

  const curriculum = course?.curriculum?.length > 0 ? course.curriculum : defaultCurriculum;

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video': return <PlayCircle className="h-4 w-4 text-violet-600" />;
      case 'document': return <FileText className="h-4 w-4 text-blue-600" />;
      case 'assignment': return <BookOpen className="h-4 w-4 text-emerald-600" />;
      default: return <CheckCircle className="h-4 w-4 text-stone-400" />;
    }
  };

  const totalLessons = curriculum.reduce((acc, module) => acc + (module.lessons?.length || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-stone-900 mb-4">Course Not Found</h2>
          <Link to="/explore-courses">
            <Button className="bg-violet-700 hover:bg-violet-800">Browse Courses</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="sticky top-0 z-30 w-full bg-white/80 backdrop-blur-xl border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <GraduationCap className="h-8 w-8 text-violet-700" />
            <span className="text-xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
              {settings?.site_name || 'LearnHub'}
            </span>
          </Link>
          <Link to="/explore-courses">
            <Button variant="outline" className="rounded-full">
              <ArrowLeft className="h-4 w-4 mr-2" />
              All Courses
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <div 
        className="relative py-16 md:py-24"
        style={{ 
          background: `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}05 100%)`
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Course Info */}
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <Badge className="bg-violet-100 text-violet-700">{course.category}</Badge>
                <Badge variant="outline" className="border-emerald-300 text-emerald-700">
                  {course.skill_level || 'All Levels'}
                </Badge>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-stone-900 leading-tight" style={{ fontFamily: 'Outfit' }}>
                {course.title}
              </h1>
              
              <p className="text-lg text-stone-600 leading-relaxed">
                {course.short_description || course.description}
              </p>

              {/* Stats */}
              <div className="flex flex-wrap items-center gap-6 text-stone-600">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-violet-600" />
                  <span>{course.duration || '8 weeks'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-violet-600" />
                  <span>{course.enrolled_count || 0} enrolled</span>
                </div>
                <div className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-violet-600" />
                  <span>{totalLessons} lessons</span>
                </div>
              </div>

              {/* Instructor */}
              <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-stone-100">
                <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center">
                  <GraduationCap className="h-6 w-6 text-violet-700" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Instructor</p>
                  <p className="font-semibold text-stone-900">{course.instructor}</p>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <Link to={`/register?course=${courseId}`}>
                  <Button 
                    size="lg" 
                    className="bg-violet-700 hover:bg-violet-800 rounded-full px-8"
                    data-testid="enroll-btn"
                  >
                    {course.price > 0 ? `Enroll Now - £${course.price}` : 'Enroll for Free'}
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="rounded-full px-8"
                  onClick={() => setShowCurriculum(!showCurriculum)}
                  data-testid="view-curriculum-btn"
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  {showCurriculum ? 'Hide Curriculum' : 'View Curriculum'}
                </Button>
              </div>
            </div>

            {/* Course Image */}
            <div className="relative">
              <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-violet-600 to-purple-700">
                {course.thumbnail ? (
                  <img 
                    src={course.thumbnail.startsWith('http') ? course.thumbnail : `${API.replace('/api', '')}${course.thumbnail}`}
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <PlayCircle className="h-24 w-24 text-white/50" />
                  </div>
                )}
              </div>
              {/* Floating card */}
              <Card className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg border-0 hidden md:block">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Award className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-stone-500">Certificate</p>
                    <p className="font-semibold text-stone-900">On Completion</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Left Column - About & Curriculum */}
          <div className="lg:col-span-2 space-y-12">
            {/* About Section */}
            <section>
              <h2 className="text-2xl font-bold text-stone-900 mb-6" style={{ fontFamily: 'Outfit' }}>
                About This Course
              </h2>
              <div className="prose prose-stone max-w-none">
                <p className="text-stone-600 leading-relaxed text-lg">
                  {course.description}
                </p>
              </div>
            </section>

            {/* Curriculum Section */}
            <section id="curriculum" className={`${showCurriculum ? 'block' : 'hidden lg:block'}`}>
              <h2 className="text-2xl font-bold text-stone-900 mb-6" style={{ fontFamily: 'Outfit' }}>
                Course Curriculum
              </h2>
              <Card className="bg-white rounded-xl border border-stone-100 overflow-hidden">
                <Accordion type="single" collapsible className="w-full" data-testid="curriculum-accordion">
                  {curriculum.map((module, moduleIndex) => (
                    <AccordionItem key={moduleIndex} value={`module-${moduleIndex}`} className="border-b border-stone-100 last:border-0">
                      <AccordionTrigger className="px-6 py-4 hover:bg-stone-50 hover:no-underline">
                        <div className="flex items-center gap-4 text-left">
                          <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center shrink-0">
                            <span className="font-bold text-violet-700">{moduleIndex + 1}</span>
                          </div>
                          <div>
                            <h3 className="font-semibold text-stone-900">{module.module_title}</h3>
                            <p className="text-sm text-stone-500">{module.lessons?.length || 0} lessons</p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-4">
                        {module.module_description && (
                          <p className="text-stone-600 mb-4 pl-14">{module.module_description}</p>
                        )}
                        <div className="space-y-2 pl-14">
                          {module.lessons?.map((lesson, lessonIndex) => (
                            <div 
                              key={lessonIndex}
                              className="flex items-center justify-between p-3 bg-stone-50 rounded-lg"
                            >
                              <div className="flex items-center gap-3">
                                {getLessonIcon(lesson.type)}
                                <span className="text-stone-700">{lesson.title}</span>
                              </div>
                              {lesson.duration && (
                                <span className="text-sm text-stone-500">{lesson.duration}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Card>
            </section>

            {/* What You'll Learn */}
            <section>
              <h2 className="text-2xl font-bold text-stone-900 mb-6" style={{ fontFamily: 'Outfit' }}>
                What You'll Learn
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  "Master core concepts and principles",
                  "Hands-on practical experience",
                  "Industry best practices",
                  "Real-world project portfolio",
                  "Expert guidance and support",
                  "Certificate of completion"
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-stone-100">
                    <CheckCircle className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                    <span className="text-stone-700">{item}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Right Column - Sticky Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <Card className="bg-white rounded-2xl border border-stone-100 shadow-lg overflow-hidden">
                <CardContent className="p-6 space-y-6">
                  {/* Price */}
                  <div className="text-center pb-6 border-b border-stone-100">
                    <p className="text-4xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
                      {course.price > 0 ? `£${course.price}` : 'Free'}
                    </p>
                    {course.price > 0 && (
                      <p className="text-sm text-stone-500 mt-1">One-time payment</p>
                    )}
                  </div>

                  {/* Features */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 text-stone-600">
                      <Clock className="h-5 w-5 text-violet-600" />
                      <span>{course.duration || '8 weeks'} of content</span>
                    </div>
                    <div className="flex items-center gap-3 text-stone-600">
                      <BookOpen className="h-5 w-5 text-violet-600" />
                      <span>{totalLessons} lessons</span>
                    </div>
                    <div className="flex items-center gap-3 text-stone-600">
                      <Users className="h-5 w-5 text-violet-600" />
                      <span>{course.enrolled_count || 0} students enrolled</span>
                    </div>
                    <div className="flex items-center gap-3 text-stone-600">
                      <Award className="h-5 w-5 text-violet-600" />
                      <span>Certificate on completion</span>
                    </div>
                  </div>

                  {/* CTA */}
                  <Link to={`/register?course=${courseId}`} className="block">
                    <Button 
                      className="w-full bg-violet-700 hover:bg-violet-800 rounded-full h-12 text-lg"
                      data-testid="sidebar-enroll-btn"
                    >
                      Enroll Now
                    </Button>
                  </Link>

                  <p className="text-center text-sm text-stone-500">
                    30-day money-back guarantee
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-stone-900 text-stone-300 py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <GraduationCap className="h-6 w-6 text-violet-400" />
            <span className="font-bold text-white">{settings?.site_name || 'LearnHub'}</span>
          </div>
          <p className="text-sm">{settings?.footer_text || '© 2024 LearnHub. All rights reserved.'}</p>
        </div>
      </footer>
    </div>
  );
};

export default CourseInfo;
